/**
 * Hub-chat shapes the SPA reads: attachment metadata echoed on chat SSE
 * events and transcript entries, and the ack returned when an operator
 * resolves a tool confirmation.
 */

import { z } from "zod";
import { ConfirmationPriorResolutionSchema } from "./confirmations.js";

/** Attachment metadata shared by transcript history and chat events. */
export const HubChatAttachmentSchema = z
  .object({
    objectKey: z.string().min(1),
    name: z.string().min(1),
    mimeType: z.string().min(1),
    size: z.number().int().nonnegative(),
    status: z.enum(["ready", "failed"]),
    expiredAt: z.string().datetime().optional(),
    /** Agent-workspace-relative reference; expiredAt overrides staging availability. */
    workspacePath: z.string().min(1).optional(),
  })
  .strict();
export type HubChatAttachment = z.infer<typeof HubChatAttachmentSchema>;

/**
 * An accepted ack carries `outcome` so a surface can say what happens next:
 * `resumed` (running now), `accepted_queued` (the session is mid-turn and a
 * durable sentinel carries the decision), or `accepted_awaiting_batch` (the
 * run parked on several tools and the others are still unanswered).
 *
 * Non-accepted acks distinguish four cases so surfaces never conflate them:
 * `already_resolved` (another decider or the expiry sweep won — `prior`
 * carries that terminal outcome, so the UI renders "Approved by X · 2m ago"
 * instead of an error), `agent_paused` (the decision remains pending until the
 * specialist resumes), `retryable` (the write failed and the same decision
 * should be tried again), and `unknown_id` (genuinely no such confirmation).
 */
export const HubChatConfirmAckSchema = z
  .object({
    accepted: z.boolean(),
    outcome: z.enum(["resumed", "accepted_queued", "accepted_awaiting_batch"]).optional(),
    reason: z.enum(["unknown_id", "already_resolved", "agent_paused", "retryable"]).optional(),
    /** Present exactly when `reason` is `already_resolved`. */
    prior: ConfirmationPriorResolutionSchema.optional(),
  })
  .strict();
export type HubChatConfirmAck = z.infer<typeof HubChatConfirmAckSchema>;

/**
 * Why a turn ended in error. Mirrors `TurnErrorKind` in the channel runtime —
 * the SPA cannot import server packages, so the browser boundary restates the
 * union rather than sharing the type.
 */
export const HubChatTurnErrorKindSchema = z.enum([
  "refusal",
  "timeout",
  "rate_limit",
  "context_length",
  "egress_refresh",
  "unknown",
]);
export type HubChatTurnErrorKind = z.infer<typeof HubChatTurnErrorKindSchema>;

/** Pre-first-token milestones, in the order a turn passes through them. */
export const HubChatTurnPhaseSchema = z.enum(["queued", "admitted", "starting_model"]);
export type HubChatTurnPhase = z.infer<typeof HubChatTurnPhaseSchema>;
